import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Settings2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import NotificationSettings from './NotificationSettings';

const POLL_MS = 20000;

const timeAgo = (d) => {
  const t = new Date(d); if (isNaN(t)) return '';
  const m = Math.round((Date.now() - t.getTime()) / 60000);
  if (m < 1) return 'recién'; if (m < 60) return `hace ${m} min`;
  const h = Math.round(m / 60); if (h < 24) return `hace ${h} h`;
  return t.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
};

function beep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext; if (!Ctx) return;
    const ctx = new Ctx(); const o = ctx.createOscillator(); const g = ctx.createGain();
    o.type = 'sine'; o.frequency.value = 880; g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    o.connect(g); g.connect(ctx.destination); o.start(); o.stop(ctx.currentTime + 0.4);
  } catch { /* sin sonido */ }
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const lastId = useRef(null);
  const baseTitle = useRef(document.title.replace(/^\(\d+\)\s*/, ''));

  const go = useCallback((n) => {
    if (!n.read_at) api.post('/notifications/read', { ids: [n.id] }).catch(() => {});
    setOpen(false);
    if (n.url) navigate(n.url);
  }, [navigate]);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications/poll', { params: { after_id: lastId.current || 0 } });
      setUnread(data.unread);
      if (lastId.current !== null && data.fresh && data.fresh.length) {
        beep();
        data.fresh.slice(0, 3).reverse().forEach(n => toast(t => (
          <div onClick={() => { toast.dismiss(t.id); go(n); }} style={{ cursor: 'pointer', maxWidth: 300 }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>🔔 {n.title}</div>
            {n.body && <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2, whiteSpace: 'pre-line' }}>{n.body}</div>}
          </div>
        ), { duration: 7000 }));
      }
      lastId.current = Math.max(lastId.current || 0, data.max_id || 0);
    } catch { /* sin conexión */ }
  }, [go]);

  useEffect(() => { poll(); const t = setInterval(poll, POLL_MS); return () => clearInterval(t); }, [poll]);
  useEffect(() => { document.title = (unread > 0 ? `(${unread}) ` : '') + baseTitle.current; }, [unread]);

  const openPanel = async () => {
    const next = !open; setOpen(next);
    if (next) { const { data } = await api.get('/notifications').catch(() => ({ data: { items: [] } })); setItems(data.items || []); }
  };
  const readAll = async () => { await api.post('/notifications/read', {}).catch(() => {}); setUnread(0); setItems(items.map(i => ({ ...i, read_at: i.read_at || new Date().toISOString() }))); };

  return (
    <>
      <button className="notif-bell-anchor btn btn-ghost" onClick={openPanel} title="Notificaciones" aria-label="Notificaciones">
        <Bell size={19} />
        {unread > 0 && <span className="notif-badge">{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1500 }} />}
      {open && (
        <div className="notif-panel">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 700 }}>Notificaciones</div>
            <div style={{ display: 'flex', gap: 2 }}>
              <button className="btn btn-ghost btn-sm" onClick={readAll} title="Marcar todas como leídas" style={{ padding: 6 }}><CheckCheck size={16} /></button>
              <button className="btn btn-ghost btn-sm" onClick={() => { setOpen(false); setSettingsOpen(true); }} title="Configurar" style={{ padding: 6 }}><Settings2 size={16} /></button>
            </div>
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {items.length === 0
              ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No tenés notificaciones</div>
              : items.map(n => (
                <div key={n.id} onClick={() => go(n)} style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', cursor: 'pointer', background: n.read_at ? 'transparent' : 'var(--accent-glow)', display: 'flex', gap: 8 }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', marginTop: 6, flexShrink: 0, background: n.read_at ? 'transparent' : 'var(--accent)' }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{n.title}</div>
                    {n.body && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, whiteSpace: 'pre-line' }}>{n.body}</div>}
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{timeAgo(n.created_at)}</div>
                  </div>
                </div>
              ))}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => { setOpen(false); setSettingsOpen(true); }} style={{ justifyContent: 'center', borderTop: '1px solid var(--border)', borderRadius: 0, padding: 10 }}>
            <Settings2 size={14} /> Configurar notificaciones
          </button>
        </div>
      )}
      {settingsOpen && <NotificationSettings onClose={() => setSettingsOpen(false)} />}
    </>
  );
}
